import { useCallback, useEffect, useRef, useState } from 'react';
import { PROTOCOL_VERSION, WS_SUPERSEDED, applyTuning, newHeldBoard, patchView, resolveView } from '@tb/shared';
import type {
  AccessState,
  AdmissionRefusal,
  BattleOpponent,
  BotSkill,
  Signal,
  ChatMessage,
  ChatTag,
  ClientMsg,
  Commitment,
  Deployment,
  Doctrine,
  EnemyCityView,
  Faction,
  LobbySummary,
  Phase,
  PlayerResolution,
  PlayerView,
  ServerMsg,
  WarSummary,
} from '@tb/shared';
import { chosenSide } from './front/side';
import { setSoundAllowed } from './audio';

// ---------------------------------------------------------------------------
// The socket. One connection for the whole session, with reconnect, because a
// two-minute phase is a long time to lose to a flaky mobile connection.
// ---------------------------------------------------------------------------

export type ConnState = 'connecting' | 'open' | 'closed' | 'superseded';

/** When this browser received something carrying `serverNow`, to run its countdowns on the server's clock. */
export interface Received {
  receivedAt: number;
}

/** The side this device chose, as a join message carries it: nothing for either. */
const withSide = (): { side?: Faction } => {
  const side = chosenSide();
  return side ? { side } : {};
};

/**
 * The decision phase an order was written in, as the view the commander
 * acted on showed it. A message queued while the connection is down goes out
 * after the next hello, and the server refuses or ignores one whose phase has
 * passed rather than applying it to a later one.
 */
export interface Stamp {
  round: number;
  phase: Phase;
}

/** The stamp of the view held, for a caller that did not say which it acted on. */
const stampOf = (view: PlayerView | null): Partial<Stamp> =>
  view ? { round: view.match.round, phase: view.match.phase } : {};

/** Milliseconds until `at` on the server's clock, as of now. */
export function serverRemaining(at: number, serverNow: number, receivedAt: number): number {
  return Math.max(0, at - serverNow - (Date.now() - receivedAt));
}

export interface Net {
  state: ConnState;
  /** Take the session back into this tab, for one that has been displaced. */
  reconnect: () => void;
  playerId: string | null;
  matches: LobbySummary[];
  /** The open war, or wars (war.ts), as the lobby shows them. */
  wars: (WarSummary & Received)[];
  /** A battle against other commanders whose call-up is open, if there is one. */
  battles: (WarSummary & Received)[];
  /**
   * Whether training and battles are open to the public, or only to testers
   * (D36). Open until the server says otherwise, as an older server always was.
   */
  access: AccessState;
  /** Waiting to be dealt into the war: in its call-up, or for the next region. */
  deployment: (Deployment & Received) | null;
  view: PlayerView | null;
  matchId: string | null;
  resolution: PlayerResolution | null;
  /**
   * The view as it stood immediately before `resolution` arrived. The replay
   * needs it to know which tiles were still dark, so impacts can be seen to
   * tear the fog open rather than the city simply being lit from the start.
   */
  priorView: PlayerView | null;
  errors: { id: number; message: string }[];
  /**
   * Why the war last turned this commander away (`error` with a code), held
   * for the lobby to say on the Play plate rather than in a passing toast.
   * Cleared by pressing Play again, or by getting in.
   */
  refusal: { code: AdmissionRefusal; message: string } | null;
  /** Every line heard on your side's chat this match, oldest first. */
  chat: ChatMessage[];
  /**
   * Every report this match, newest last, including the plan and defence
   * phases' quieter ones that never open as an overlay.
   */
  log: PlayerResolution[];
  send: (msg: ClientMsg) => void;
  /** Say a line to your whole side. */
  say: (text: string, opts?: { tag?: ChatTag; refs?: string[] }) => void;
  /** Your city's status cards for your side, replacing the last set. */
  signals: (signals: Signal[]) => void;
  /**
   * Announce this commander, now if the socket is open. Every socket after
   * that announces them as it opens, and nothing else does: one hello, and
   * one whole view back, per connection.
   */
  hello: (name: string) => void;
  /**
   * Play, a battle or Train again pressed from inside a match, and the server
   * not yet saying where to. It puts the match down (`left`) a moment before
   * it says, and the lobby is not where the commander is going: the shell
   * holds on Preparing command until it says, or turns them away.
   */
  leaving: boolean;
  /** Into the war, whatever that means right now: the server decides. A newcomer needs the password for now (front/gate.ts). */
  play: (password?: string) => void;
  /**
   * Command training: a private 1v1 against a Recruit (docs/interface/TRAINING.md).
   * `password`: the tester word, needed only while the game is closed (D36).
   */
  train: (password?: string) => void;
  /** A battle (MatchConfig.cluster): against NPCs at `skill`, or the next call-up against other commanders. `password` as for train. */
  battle: (vs: BattleOpponent, skill?: BotSkill, password?: string) => void;
  createMatch: (perSide: number, phaseSeconds: number) => void;
  /** Build a match of NPCs only and watch it from one side. */
  /** `password`: the simulation room's word (server simGate.ts). */
  /** `battle`: one 4v4 cluster without ICBMs, in place of `perSide`'s regions. */
  createSim: (perSide: number, phaseSeconds: number, west: BotSkill, east: BotSkill, watch: Faction, password: string, battle?: boolean) => void;
  /** Watch a running match; `side` locks a simulation watcher to one side. */
  spectate: (id: string, side?: Faction) => void;
  /** Watch over a different commander's shoulder. */
  follow: (playerId: string) => void;
  /** Spectator-only: lift the fog on the far side of the board. */
  reveal: (on: boolean) => void;
  joinMatch: (id: string) => void;
  leaveMatch: () => void;
  /** Start the war; empty seats go to NPCs of this skill. */
  startMatch: (fill: BotSkill) => void;
  addBot: (skill: BotSkill) => void;
  fillWithBots: (skill: BotSkill) => void;
  removePlayer: (playerId: string) => void;
  /** Lock orders in, stamped with the phase they were written for: `at`, or the view held now. */
  commit: (c: Commitment, at?: Stamp) => void;
  /**
   * Tell the server this client has finished watching its replay. `round` is
   * the round the replay hold is in, the one after the exchange replayed: the
   * server ignores an acknowledgement naming any other. Not taken from the
   * view held, which for a moment after the report is still the exchange's
   * own round (useReplayFlow waits for the next); without it the
   * acknowledgement goes unstamped, as before stamps.
   */
  replayDone: (round?: number) => void;
  /** Watching a simulation: set the stance of the commander being followed. */
  setDoctrine: (d: Doctrine) => void;
  /** Withdraw locked orders, stamped like a commit: a stale one is ignored. */
  uncommit: (at?: Stamp) => void;
  /**
   * Mirror the orders drafted so far to the server: they go in if the clock
   * runs out. Returns the draft's number among those sent, for matching it
   * to `draftAck`. Never queued while the connection is down: the mirror
   * sends its latest again once the reconnect's view arrives (`link`), and a
   * queued draft written for a phase since passed would land in a room that
   * does not check its stamp.
   */
  draft: (c: Commitment, at: Stamp) => number;
  /**
   * Whether the server took the last draft sent, when it said so, and how
   * many drafts had been sent by then: it can only be answering one of those.
   */
  draftAck: { ok: boolean; reason?: string; at: number; drafts: number } | null;
  /**
   * Which connection this is, counted when its first whole view arrives: the
   * server's word, after a reconnect, on what it still holds. Anything sent
   * on the last connection may have gone down with it.
   */
  link: number;
  dismissResolution: () => void;
}

const TOKEN_KEY = 'tb.token';
/** Lines of chat held in memory; the server holds the rest. */
const CHAT_HELD = 300;
/** Reports held for the reports workspace. */
const LOG_HELD = 16;
/** How long the shell holds on Preparing command for an answer that does not come (Net.leaving). */
const LEAVING_MS = 10000;
/** The first reconnect comes quickly: most drops are a moment's handover between networks. */
const RETRY_FIRST_MS = 1000;
/** However long the server has been gone, a try at least this often. */
const RETRY_MAX_MS = 10000;

/**
 * How long to wait before reconnect attempt `attempt`, from 0: doubling from
 * RETRY_FIRST_MS up to RETRY_MAX_MS, and each wait anywhere from half to all
 * of that. A fixed 1.5 s had every tab try forty times a minute for as long
 * as the server was down, and a restart was met by all of them at once.
 */
export function retryDelay(attempt: number, random: () => number = Math.random): number {
  const ceiling = Math.min(RETRY_MAX_MS, RETRY_FIRST_MS * 2 ** Math.max(0, attempt));
  return Math.round(ceiling * (0.5 + random() / 2));
}

/**
 * Whether this message takes the connection out of the match it holds
 * (`held`, by id): leaving it, joining another, or a place in another's
 * queue to deploy. Play from the end of training or a finished match used to
 * bring only the war's deployment, and the room that was over stayed on
 * screen in front of the call-up.
 */
export function leavesMatch(msg: ServerMsg, held: string | null): boolean {
  switch (msg.t) {
    case 'left': return true;
    case 'joined': return msg.matchId !== held;
    case 'deployment': return msg.deployment != null && held != null && msg.deployment.matchId !== held;
    default: return false;
  }
}

/** Merge lines by their sequence number: a backlog can overlap what arrived live. */
function mergeChat(held: ChatMessage[], add: ChatMessage[]): ChatMessage[] {
  const bySeq = new Map(held.map((m) => [m.seq, m]));
  for (const m of add) bySeq.set(m.seq, m);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq).slice(-CHAT_HELD);
}

/**
 * A stable identity for this browser, kept across reloads.
 *
 * The server keys a seat on this rather than on the socket, so a refresh, a
 * crash or a lost connection puts you back in the same city instead of back in
 * the lobby. Local storage can be unavailable — a private window, storage
 * blocked — in which case the session is simply not resumable, which is what
 * the old behaviour was anyway.
 */
function sessionToken(): string | undefined {
  try {
    let t = localStorage.getItem(TOKEN_KEY);
    if (!t || !/^[A-Za-z0-9_-]{16,128}$/.test(t)) {
      t = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)
        .replace(/[^A-Za-z0-9_-]/g, '')
        .slice(0, 64);
      localStorage.setItem(TOKEN_KEY, t);
    }
    return t;
  } catch {
    return undefined;
  }
}

/**
 * Forget this browser's guest identity. The next connection is a new player:
 * any seat the old identity held carries on under an NPC, and nobody on this
 * device can resume it. For a shared or borrowed device.
 */
export function forgetIdentity(): void {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* nothing stored to forget */ }
}

function helloMsg(name: string): ClientMsg {
  return { t: 'hello', name, token: sessionToken(), protocol: PROTOCOL_VERSION };
}

function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws`;
}

export function useNet(): Net {
  const [state, setState] = useState<ConnState>('connecting');
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [matches, setMatches] = useState<LobbySummary[]>([]);
  const [access, setAccess] = useState<AccessState>({ open: true });
  const [wars, setWars] = useState<(WarSummary & Received)[]>([]);
  const [battles, setBattles] = useState<(WarSummary & Received)[]>([]);
  const [deployment, setDeployment] = useState<(Deployment & Received) | null>(null);
  const [view, setView] = useState<PlayerView | null>(null);
  const [matchId, setMatchId] = useState<string | null>(null);
  const [resolution, setResolution] = useState<PlayerResolution | null>(null);
  const [priorView, setPriorView] = useState<PlayerView | null>(null);
  const [errors, setErrors] = useState<{ id: number; message: string }[]>([]);
  const [refusal, setRefusal] = useState<Net['refusal']>(null);
  const [draftAck, setDraftAck] = useState<Net['draftAck']>(null);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [log, setLog] = useState<PlayerResolution[]>([]);
  const [link, setLink] = useState(0);

  const sock = useRef<WebSocket | null>(null);
  // The server sends the resolution before the refreshed view, but React state
  // is not readable from inside the socket handler, so mirror it in a ref.
  const viewRef = useRef<PlayerView | null>(null);
  /** The match this connection is in, mirrored for the same reason. */
  const matchIdRef = useRef<string | null>(null);
  /**
   * Every enemy city of this match as last sent, and every building and tree
   * layout by key, for views that name one rather than resend it.
   */
  const heldBoard = useRef(newHeldBoard());
  /**
   * Orders sent to be locked in, by the ref they went with. The server says
   * which are locked by that ref rather than sending them back.
   */
  const sentOrders = useRef(new Map<number, Commitment>());
  const orderRef = useRef(0);
  /** Drafts sent so far, to say which of them an acknowledgement can be about. */
  const draftsSent = useRef(0);
  const queue = useRef<ClientMsg[]>([]);
  const nameRef = useRef<string>('');
  const errId = useRef(0);
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Reconnect attempts since a socket last opened. */
  const attempts = useRef(0);
  const [leaving, setLeaving] = useState(false);
  const leavingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Told where to, or turned away: the hold on Preparing command is over. */
  const settle = useCallback(() => {
    if (leavingTimer.current) clearTimeout(leavingTimer.current);
    leavingTimer.current = null;
    setLeaving(false);
  }, []);
  /** On the way out of the match held to another; from the front there is nothing to hold. */
  const leave = () => {
    if (!matchIdRef.current) return;
    if (leavingTimer.current) clearTimeout(leavingTimer.current);
    setLeaving(true);
    leavingTimer.current = setTimeout(settle, LEAVING_MS);
  };

  const send = useCallback((msg: ClientMsg) => {
    const s = sock.current;
    if (s && s.readyState === WebSocket.OPEN) s.send(JSON.stringify(msg));
    else queue.current.push(msg);
  }, []);

  const connect = useCallback(() => {
    if (retry.current) clearTimeout(retry.current);
    retry.current = null;
    const previous = sock.current;
    sock.current = null;
    previous?.close();
    setState('connecting');
    const s = new WebSocket(wsUrl());
    sock.current = s;
    // Until this socket's first whole view, the last connection's word on
    // what the server holds is all there is.
    let caughtUp = false;

    s.onopen = () => {
      if (sock.current !== s) return;
      attempts.current = 0;
      setState('open');
      // Re-announce ourselves, then flush anything typed while we were down.
      // The token is what makes that a resumption rather than a new arrival:
      // the server answers with the match still in progress, if there is one.
      // Before a name is given there is nothing to announce; hello() says it.
      if (nameRef.current) s.send(JSON.stringify(helloMsg(nameRef.current)));
      const pending = queue.current.splice(0);
      for (const m of pending) s.send(JSON.stringify(m));
    };

    s.onmessage = (ev) => {
      if (sock.current !== s) return;
      let msg: ServerMsg;
      try {
        msg = JSON.parse(ev.data as string) as ServerMsg;
      } catch {
        return;
      }
      // Out of one match and on to the next, or back to the front: nothing of
      // the last may carry over. Training's reports came into the war's first
      // rounds, its feed and its impacts on the war's stage, and Train again
      // went straight past the coach's opening.
      if (leavesMatch(msg, matchIdRef.current ?? viewRef.current?.match.id ?? null)) {
        // The server forgets what it sent of the last match's cities here too.
        heldBoard.current = newHeldBoard();
        viewRef.current = null;
        matchIdRef.current = null;
        setMatchId(null);
        setView(null);
        setResolution(null);
        setPriorView(null);
        setChat([]);
        setLog([]);
        setDraftAck(null);
      }
      switch (msg.t) {
        case 'welcome':
          // The server's numbers, so every price and preview here matches the
          // rules it plays by rather than the ones this bundle was built with.
          if (msg.tuning) {
            try { applyTuning(msg.tuning); } catch (e) { console.error('Could not apply the server\'s tuning', e); }
          }
          setPlayerId(msg.playerId);
          // The owner's sound switch (D37): off unless the server says on.
          setSoundAllowed(msg.sound === true);
          break;
        case 'sound':
          setSoundAllowed(msg.on);
          break;
        case 'matches': {
          setMatches(msg.matches);
          const receivedAt = Date.now();
          setWars((msg.wars ?? []).map((w) => ({ ...w, receivedAt })));
          setBattles((msg.battles ?? []).map((w) => ({ ...w, receivedAt })));
          setAccess(msg.access ?? { open: true });
          break;
        }
        case 'deployment':
          setDeployment(msg.deployment && { ...msg.deployment, receivedAt: Date.now() });
          if (msg.deployment) { setRefusal(null); settle(); }
          break;
        case 'joined':
          settle();
          // Back into the same match after a reconnect keeps its reports and
          // chat (leavesMatch), but the server starts its board afresh.
          heldBoard.current = newHeldBoard();
          setRefusal(null);
          setDeployment(null);
          setDraftAck(null);
          matchIdRef.current = msg.matchId;
          setMatchId(msg.matchId);
          break;
        case 'left':
          // Still waiting to deploy, the server says so straight after.
          setDeployment(null);
          break;
        case 'view': {
          // Enemy cities unchanged since this client was last sent them come
          // by name only, and layouts by key: both filled in from what it holds.
          const view = resolveView(msg.view, heldBoard.current);
          if (!view) {
            // Lost track somehow: ask for the whole board rather than show a hole.
            send({ t: 'resync' });
            break;
          }
          viewRef.current = view;
          setView(view);
          if (!caughtUp) { caughtUp = true; setLink((n) => n + 1); }
          break;
        }
        case 'commit':
        case 'replay':
        case 'clock':
        case 'roster':
        case 'signals':
        case 'side': {
          // The line of the board that changed: a commit, the replay hold,
          // the clock, the roster, the side from above. Patched into the view
          // already held rather than waiting for a new one, which is the whole
          // fogged board and far too heavy to resend for any of these.
          const held = viewRef.current;
          let delta: ServerMsg = msg;
          if (msg.t === 'commit' && msg.ref != null) {
            // Our own orders, locked: put back the copy we sent.
            const mine = sentOrders.current.get(msg.ref);
            for (const ref of sentOrders.current.keys()) if (ref <= msg.ref) sentOrders.current.delete(ref);
            if (!mine) { send({ t: 'resync' }); break; }
            delta = { ...msg, commitment: mine };
          }
          const patched = held && patchView(held, delta, heldBoard.current);
          if (!patched) {
            if (msg.t === 'side' && held?.match.id === msg.matchId) send({ t: 'resync' });
            break;
          }
          viewRef.current = patched;
          setView(patched);
          break;
        }
        case 'chat':
        case 'chatBacklog': {
          // Only for the war on screen: a line that arrives after leaving must
          // not bring the old war's traffic back.
          if (viewRef.current?.match.id !== msg.matchId) break;
          const add = msg.t === 'chat' ? [msg.message] : msg.messages;
          setChat((held) => mergeChat(held, add));
          break;
        }
        case 'resolution':
          setLog((held) => [
            ...held.filter((r) => !(r.round === msg.resolution.round && r.phase === msg.resolution.phase)),
            msg.resolution,
          ].slice(-LOG_HELD));
          // Plan, Attack and Defence updates belong in history. Only the
          // completed exchange opens the replay and its round briefing.
          if (msg.resolution.phase === 'RESOLUTION') {
            // Snapshot before the next view lands and overwrites the fog.
            setPriorView(viewRef.current);
            setResolution(msg.resolution);
          }
          break;
        case 'draftAck':
          // One from a room just left is not about the drafts on screen.
          if (viewRef.current?.match.id !== msg.matchId) break;
          setDraftAck({ ok: msg.ok, reason: msg.reason, at: Date.now(), drafts: draftsSent.current });
          break;
        case 'error': {
          settle();
          // The war turning somebody away is said on the Play plate (Lobby).
          if (msg.code) { setRefusal({ code: msg.code, message: msg.message }); break; }
          const id = ++errId.current;
          setErrors((e) => [...e, { id, message: msg.message }]);
          setTimeout(() => setErrors((e) => e.filter((x) => x.id !== id)), 5000);
          break;
        }
      }
    };

    s.onclose = (ev) => {
      if (sock.current !== s) return;
      settle();
      // Superseded is not a network problem, and reconnecting on a timer makes
      // it one: this socket would take the seat straight back off whichever tab
      // displaced it, that tab would reconnect, and the two would trade the
      // session between them forever with a banner up the whole time. Sit still
      // and let the player say which tab they meant.
      if (ev.code === WS_SUPERSEDED) {
        setState('superseded');
        return;
      }
      setState('closed');
      if (retry.current) clearTimeout(retry.current);
      retry.current = setTimeout(connect, retryDelay(attempts.current++));
    };

    s.onerror = () => s.close();
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (retry.current) clearTimeout(retry.current);
      retry.current = null;
      if (leavingTimer.current) clearTimeout(leavingTimer.current);
      const s = sock.current;
      sock.current = null;
      s?.close();
    };
  }, [connect]);

  // Keep-alive: mobile browsers will drop an idle socket during a quiet phase.
  useEffect(() => {
    const h = setInterval(() => send({ t: 'ping' }), 25000);
    return () => clearInterval(h);
  }, [send]);

  return {
    reconnect: connect,
    state,
    playerId,
    matches,
    wars,
    battles,
    access,
    deployment,
    view,
    matchId,
    resolution,
    priorView,
    errors,
    refusal,
    draftAck,
    link,
    leaving,
    chat,
    log,
    send,
    say: (text, opts) => send({
      t: 'say', text,
      ...(opts?.tag ? { tag: opts.tag } : {}),
      ...(opts?.refs?.length ? { refs: opts.refs } : {}),
    }),
    hello: (name) => {
      nameRef.current = name;
      // Not queued: a socket not open yet says it as it opens.
      const s = sock.current;
      if (s && s.readyState === WebSocket.OPEN) s.send(JSON.stringify(helloMsg(name)));
    },
    // Every way in carries the side this device chose, wherever it is pressed:
    // the lobby, the story's last page, or training's end.
    play: (password) => { setRefusal(null); leave(); send({ t: 'play', ...(password ? { password } : {}), ...withSide() }); },
    train: (password) => { setRefusal(null); leave(); send({ t: 'train', ...(typeof password === 'string' && password ? { password } : {}), ...withSide() }); },
    battle: (vs, skill, password) => { setRefusal(null); leave(); send({ t: 'battle', vs, ...(skill ? { skill } : {}), ...(password ? { password } : {}), ...withSide() }); },
    createMatch: (perSide, phaseSeconds) => send({ t: 'createMatch', perSide, phaseSeconds }),
    createSim: (perSide, phaseSeconds, west, east, watch, password, battle) =>
      send({ t: 'createSim', perSide, phaseSeconds, west, east, watch, password, ...(battle ? { battle: true } : {}) }),
    spectate: (id, side) => send({ t: 'spectate', matchId: id, ...(side ? { side } : {}) }),
    follow: (playerId) => send({ t: 'follow', playerId }),
    reveal: (on) => send({ t: 'reveal', on }),
    signals: (signals) => send({ t: 'signals', signals }),
    joinMatch: (id) => send({ t: 'joinMatch', matchId: id }),
    leaveMatch: () => send({ t: 'leaveMatch' }),
    startMatch: (fill) => send({ t: 'startMatch', fill }),
    addBot: (skill) => send({ t: 'addBot', skill }),
    fillWithBots: (skill) => send({ t: 'fillWithBots', skill }),
    removePlayer: (playerId) => send({ t: 'removePlayer', playerId }),
    commit: (commitment, at) => {
      const ref = ++orderRef.current;
      sentOrders.current.set(ref, commitment);
      send({ t: 'commit', commitment, ref, ...(at ?? stampOf(viewRef.current)) });
    },
    replayDone: (round) => send({ t: 'replayDone', ...(round != null ? { round } : {}) }),
    setDoctrine: (doctrine) => send({ t: 'setDoctrine', doctrine }),
    uncommit: (at) => send({ t: 'uncommit', ...(at ?? stampOf(viewRef.current)) }),
    draft: (commitment, at) => {
      const n = ++draftsSent.current;
      const s = sock.current;
      if (s && s.readyState === WebSocket.OPEN) s.send(JSON.stringify({ t: 'draft', commitment, ...at } satisfies ClientMsg));
      return n;
    },
    dismissResolution: () => { setResolution(null); setPriorView(null); },
  };
}
